import { ColumnsType } from 'antd/lib/table'
import ColorProofNumber from '../../../../../components/color-proof-number/color-proof-number'
import { renderShipmentCalculatedValue } from '../../../../../components/shipment-calculated-value/render-shipment-calculated-value'
import formatNumber from '../../../../../helpers/formatters/format-any-number'
import renderDate from '../../../../../helpers/tables/render-date'
import { MaterialRequirement } from '../../../../../types/api/components/material/material-requirements'
import PrintQueueEntry from '../../../../../types/api/print-queue-entry'
import ShipmentPosition from '../../../../../types/api/shipment-position'
import { createAvailabilityColumns } from '../../constants/availability-columns'
import {
    createStampTubeColumn,
    printQueueFormSetIdColumn,
    createAdditionalJobsColumn,
    createAdditionalInfoColumn,
    createCuratorPrintColumn,
} from '../../constants/columns'
import { MaterialColumnCell } from '../cells/material-column-cell'
import TsAndLm from '../cells/ts-and-lm'
import ActionsCell from './components/machine-queue/components/actions-cell'
import DragHandler from './components/machine-queue/components/drag-handler'
import RelationsCell from './components/machine-queue/components/relations-cell'
import PrintJobNoteCell from '../cells/print-job-note-cell'
import EndTimeCell from './components/machine-queue/components/end-time-cell'
import QueueCirculationTimeCell from '../cells/queue-circulation-time-cell'
import { formatRollerDisplay } from '../../../../../helpers/format-roller-display'

export function createEndTimeColumn(endTimes: Map<string, string>) {
    return {
        key: 'endAt',
        title: 'Время окончания',
        width: 70,
        render(_: unknown, record: PrintQueueEntry) {
            return <EndTimeCell record={record} endAt={endTimes.get(record.documentId)} />
        },
    }
}

export function insertEndTimeColumn(
    tableColumns: ColumnsType<PrintQueueEntry>,
    endTimes: Map<string, string>
) {
    const commonTimeIndex = tableColumns.findIndex(
        col =>
            col &&
            'dataIndex' in col &&
            Array.isArray(col.dataIndex) &&
            col.dataIndex.join('.') === 'print_job.shipment.calculated.commonTime'
    )
    const insertIndex = commonTimeIndex === -1 ? tableColumns.length : commonTimeIndex + 1

    return [
        ...tableColumns.slice(0, insertIndex),
        createEndTimeColumn(endTimes),
        ...tableColumns.slice(insertIndex),
    ]
}

// TODO: нужно примерное время начала печати каждму?
export const getColumns = (
    canEdit: boolean,
    executionOrderConflicts?: Map<string, string>
): ColumnsType<PrintQueueEntry> => [
    { key: 'sort', align: 'center', width: 30, render: () => <DragHandler /> },
    {
        dataIndex: ['print_job', 'id'],
        title: 'Номер',
        width: 68,
        render: (id, record: PrintQueueEntry) => (
            <ColorProofNumber shipmentType={record.print_job?.shipment?.shipmentType}>
                {id}
            </ColorProofNumber>
        ),
    },
    {
        dataIndex: ['print_job', 'shipment', 'clientDeliveryDate'],
        title: 'Дата поставки',
        width: 70,
        render: renderDate,
    },
    { dataIndex: ['print_job', 'shipment', 'client', 'name'], title: 'Клиент' },
    createAdditionalInfoColumn<PrintQueueEntry>(record => record.print_job?.shipment),
    {
        dataIndex: ['print_job', 'shipment', 'shipment_positions'],
        title: 'Тираж',
        render(v: ShipmentPosition[]) {
            return formatNumber((v ?? []).reduce((acc, v) => acc + (v?.copies ?? 0), 0))
        },
    },
    {
        dataIndex: ['print_job', 'shipment', 'materialRequirements'],
        title: 'Материал',
        width: 100,
        render(v: MaterialRequirement[], record) {
            return <MaterialColumnCell value={v} shipment={record.print_job?.shipment} />
        },
    },
    {
        dataIndex: ['print_job', 'shipment', 'materialRequirements'],
        title: 'ТС / ЛМ',
        width: 60,
        render(v: MaterialRequirement[]) {
            return <TsAndLm materialRequirements={v} />
        },
    },
    createCuratorPrintColumn<PrintQueueEntry>(record => record.print_job?.shipment),
    {
        dataIndex: ['print_job', 'shipment', 'calculated', 'materialLength'],
        title: 'Метраж',
        width: 60,
        render(_v, r: PrintQueueEntry) {
            return renderShipmentCalculatedValue(r.print_job?.shipment, calculated =>
                Math.round((calculated?.materialLength || 0) * 1000) / 1000
            )
        },
    },
    printQueueFormSetIdColumn,
    createStampTubeColumn<PrintQueueEntry>(record => record.print_job?.shipment),
    {
        dataIndex: ['print_job', 'shipment', 'stamp', 'roller', 'roller'],
        title: 'Вал',
        render: (v: number) => formatRollerDisplay(v),
    },
    createAdditionalJobsColumn(['print_job', 'shipment']),
    createAvailabilityColumns<PrintQueueEntry>(record => record.print_job?.shipment),
    {
        dataIndex: ['print_job', 'shipment', 'calculated', 'commonTime'],
        title: 'Время тиража',
        width: 56,
        render(v, r) {
            return <QueueCirculationTimeCell record={r} />
        },
    },
    {
        dataIndex: ['print_job', 'print_queue_entries'],
        title: 'Связи',
        width: 44,
        render(v, record) {
            return (
                <RelationsCell
                    printQueueEntries={v}
                    currentEntry={record}
                    conflictMessage={executionOrderConflicts?.get(record.documentId)}
                />
            )
        },
    },
    {
        title: 'Примечание',
        width: 140,
        render(_, record) {
            return <PrintJobNoteCell printJob={record.print_job!} editable={canEdit} />
        },
    },
    {
        dataIndex: 'actions',
        title: '',
        fixed: 'right',
        render(v, r) {
            return <ActionsCell printQueueEntry={r} />
        },
    },
]
